import type { IntegerAmount } from '@actual-app/core/shared/util';

import { clasificarMes, esGastoPuntual, mediana } from './estadisticaRobusta';
import type { Clasificacion } from './estadisticaRobusta';

/**
 * Cálculos puros de la pestaña «Informes» (móvil). Todo en céntimos enteros.
 * Un «movimiento» es la suma de las transacciones de un mes × categoría ×
 * beneficiario, con el signo original (gasto negativo, ingreso positivo).
 */

export type Movimiento = {
  mes: string; // 'yyyy-MM'
  categoria: string | null;
  payee: string | null;
  importe: IntegerAmount;
};

export type CategoriaInfo = {
  id: string;
  nombre: string;
  grupo: string | null;
  nombreGrupo: string;
  esIngreso: boolean;
  oculta: boolean;
};

export type Categorias = ReadonlyMap<string, CategoriaInfo>;

export type FilaGasto = {
  categoria: string | null;
  importe: IntegerAmount; // gasto en positivo
};

export type DesgloseGasto = {
  total: IntegerAmount; // gasto total en positivo
  filas: FilaGasto[]; // ordenadas de mayor a menor gasto
  ingresosPositivos: FilaGasto[]; // categorías de gasto con saldo neto positivo
};

function esDeIngreso(categoria: string | null, categorias: Categorias) {
  return categoria != null && (categorias.get(categoria)?.esIngreso ?? false);
}

/**
 * Gasto por categoría en un conjunto de meses. Las categorías de ingreso no
 * cuentan; las de gasto cuyo neto sale positivo (devoluciones) van aparte,
 * como «ingresos positivos», para que la barra apilada no se descuadre.
 *
 * Lo que no tiene categoría no se neta: sus salidas suman al gasto y sus
 * entradas van aparte. Si no, un sueldo sin categorizar se comía el gasto sin
 * categorizar y el «Total gastado» salía corto.
 */
export function gastoPorCategoria(
  movimientos: readonly Movimiento[],
  meses: readonly string[],
  categorias: Categorias,
): DesgloseGasto {
  const conjunto = new Set(meses);
  const neto = new Map<string | null, IntegerAmount>();
  let sinCategoriaEntradas = 0;
  for (const m of movimientos) {
    if (!conjunto.has(m.mes) || esDeIngreso(m.categoria, categorias)) {
      continue;
    }
    if (m.categoria == null && m.importe > 0) {
      sinCategoriaEntradas += m.importe;
      continue;
    }
    neto.set(m.categoria, (neto.get(m.categoria) ?? 0) + m.importe);
  }

  const filas: FilaGasto[] = [];
  const ingresosPositivos: FilaGasto[] = [];
  let total = 0;
  for (const [categoria, importe] of neto) {
    if (importe < 0) {
      filas.push({ categoria, importe: -importe });
      total += -importe;
    } else if (importe > 0) {
      ingresosPositivos.push({ categoria, importe });
    }
  }
  if (sinCategoriaEntradas > 0) {
    ingresosPositivos.push({ categoria: null, importe: sinCategoriaEntradas });
  }
  filas.sort((a, b) => b.importe - a.importe);
  ingresosPositivos.sort((a, b) => b.importe - a.importe);
  return { total, filas, ingresosPositivos };
}

export type MesIngresosGastos = {
  mes: string;
  ingresos: IntegerAmount; // positivo
  gastos: IntegerAmount; // positivo (neto de las categorías de gasto)
  ahorro: IntegerAmount; // ingresos − gastos
  tasa: number | null; // (ingresos − gastos) / ingresos, en fracción
};

/**
 * Tasa de ahorro = (ingresos − gastos) / ingresos, acotada entre 0 y 1 (H84):
 * gastar más de lo ingresado es un 0 %, no un −300 %, y devoluciones grandes
 * no dan más del 100 %. Sin ingresos no hay tasa. El signo real va en
 * `ahorro`.
 */
export function tasaDeAhorro(
  ingresos: IntegerAmount,
  gastos: IntegerAmount,
): number | null {
  if (ingresos <= 0) {
    return null;
  }
  return Math.min(1, Math.max(0, (ingresos - gastos) / ingresos));
}

export function ingresosGastosPorMes(
  movimientos: readonly Movimiento[],
  meses: readonly string[],
  categorias: Categorias,
): MesIngresosGastos[] {
  const porMes = new Map<string, { ingresos: number; gastos: number }>();
  for (const mes of meses) {
    porMes.set(mes, { ingresos: 0, gastos: 0 });
  }
  for (const m of movimientos) {
    const acumulado = porMes.get(m.mes);
    if (!acumulado) {
      continue;
    }
    if (esDeIngreso(m.categoria, categorias)) {
      acumulado.ingresos += m.importe;
    } else if (m.categoria == null && m.importe > 0) {
      // Una entrada sin categoría (el sueldo recién importado) no es un gasto
      // negativo: restarla dejaba los gastos en negativo y la tasa en 35.191 %
      // (H84). Hasta que se categorice no cuenta ni como ingreso ni como gasto.
      continue;
    } else {
      acumulado.gastos -= m.importe;
    }
  }
  return meses.map(mes => {
    const { ingresos, gastos } = porMes.get(mes) ?? { ingresos: 0, gastos: 0 };
    return {
      mes,
      ingresos,
      gastos,
      ahorro: ingresos - gastos,
      tasa: tasaDeAhorro(ingresos, gastos),
    };
  });
}

export type ResumenAhorro = {
  /** Último mes completo: la cifra principal. */
  cerrado: MesIngresosGastos | null;
  /** Mes en curso («hasta hoy»), solo como dato secundario. */
  enCurso: MesIngresosGastos | null;
  /** Mediana de la tasa en los meses anteriores al cerrado. */
  habitual: number | null;
};

/**
 * Qué tasa enseñar (H84). A principio de mes el sueldo ya ha entrado y casi
 * no hay gastos: la tasa del mes en curso sale inflada. La cifra principal es
 * la del último mes completo; la del mes en curso va aparte, en neutro.
 * `porMes` va en orden cronológico y acaba en el mes en curso.
 */
export function resumenAhorro(
  porMes: readonly MesIngresosGastos[],
): ResumenAhorro {
  const enCurso = porMes[porMes.length - 1] ?? null;
  const cerrado = porMes[porMes.length - 2] ?? null;
  const previas = porMes
    .slice(0, -2)
    .map(m => m.tasa)
    .filter((x): x is number => x !== null);
  const habitual =
    previas.length > 0 ? mediana(previas.map(x => x * 1000)) / 1000 : null;
  return { cerrado, enCurso, habitual };
}

/**
 * Importe de una categoría mes a mes, alineado con `meses`. Para categorías
 * de gasto devuelve el gasto en positivo; para las de ingreso, el ingreso.
 */
export function evolucionCategoria(
  movimientos: readonly Movimiento[],
  categoria: string | null,
  meses: readonly string[],
  esIngreso = false,
): IntegerAmount[] {
  const porMes = new Map<string, number>();
  for (const m of movimientos) {
    if (m.categoria !== categoria) {
      continue;
    }
    porMes.set(m.mes, (porMes.get(m.mes) ?? 0) + m.importe);
  }
  return meses.map(mes => {
    const neto = porMes.get(mes) ?? 0;
    // `0 - 0` daría -0 y se formatearía como «-0,00».
    return neto === 0 ? 0 : esIngreso ? neto : -neto;
  });
}

/** Media aritmética; 0 si no hay valores. Redondeada al céntimo. */
export function media(valores: readonly number[]): number {
  if (valores.length === 0) {
    return 0;
  }
  return Math.round(valores.reduce((a, b) => a + b, 0) / valores.length);
}

/**
 * Desviación de un valor frente a su media, en fracción (0,25 = un 25 % por
 * encima). `null` cuando no hay media con la que comparar.
 */
export function desviacionPct(valor: number, mediaRef: number): number | null {
  if (mediaRef === 0) {
    return null;
  }
  return (valor - mediaRef) / Math.abs(mediaRef);
}

export type FilaRanking = {
  categoria: string;
  actual: IntegerAmount; // gasto del mes, en positivo
  /** Lo habitual: mediana de los meses previos sin atípicos. */
  referencia: IntegerAmount;
  desviacion: IntegerAmount; // actual − referencia (positivo = sube)
  desviacionPct: number | null;
  /** sube/baja, puntual (gasto puntual) o normal. */
  tipo: Clasificacion;
  /** El cambio se repite (≥ 2 de los últimos 3 meses): es tendencia. */
  seRepite: boolean;
};

/**
 * Qué sube y qué baja: para cada categoría de gasto, el gasto del mes frente a
 * lo habitual de los meses previos (mediana robusta; los meses atípicos no
 * cuentan). Solo entran categorías con referencia ≥ `minimoReferencia`.
 * Ordenado por desviación en € de mayor subida a mayor bajada.
 *
 * Con `mayorMovimiento` (salida más grande del mes por categoría, en
 * positivo) manda la regla explicable de `esGastoPuntual`: si la categoría
 * sube por encima de lo habitual y un solo movimiento explica > 70 % del
 * cambio, es `puntual`; si no, es `sube` (aunque sea el primer mes:
 * `seRepite` lo distingue). Sin el mapa, se usa solo la estadística.
 */
export function rankingSubeBaja(
  movimientos: readonly Movimiento[],
  mes: string,
  mesesPrevios: readonly string[],
  categorias: Categorias,
  minimoReferencia: IntegerAmount = 1000,
  mayorMovimiento?: ReadonlyMap<string, IntegerAmount>,
): FilaRanking[] {
  if (mesesPrevios.length === 0) {
    return [];
  }
  const indicePrevio = new Map(mesesPrevios.map((m, i) => [m, i] as const));
  const actual = new Map<string, number>();
  const previos = new Map<string, number[]>();
  for (const m of movimientos) {
    if (m.categoria == null || esDeIngreso(m.categoria, categorias)) {
      continue;
    }
    if (m.mes === mes) {
      actual.set(m.categoria, (actual.get(m.categoria) ?? 0) - m.importe);
      continue;
    }
    const i = indicePrevio.get(m.mes);
    if (i === undefined) {
      continue;
    }
    let serie = previos.get(m.categoria);
    if (!serie) {
      serie = mesesPrevios.map(() => 0);
      previos.set(m.categoria, serie);
    }
    serie[i] -= m.importe;
  }

  const filas: FilaRanking[] = [];
  for (const [categoria, serie] of previos) {
    const gastoActual = actual.get(categoria) ?? 0;
    const clasificacion = clasificarMes(gastoActual, serie);
    const { referencia } = clasificacion;
    if (referencia.referencia < minimoReferencia) {
      continue;
    }
    let tipo = clasificacion.tipo;
    if (mayorMovimiento && (tipo === 'sube' || tipo === 'puntual')) {
      tipo = esGastoPuntual(
        gastoActual,
        referencia.referencia,
        mayorMovimiento.get(categoria) ?? 0,
      )
        ? 'puntual'
        : 'sube';
    }
    filas.push({
      categoria,
      actual: gastoActual,
      referencia: referencia.referencia,
      desviacion: gastoActual - referencia.referencia,
      desviacionPct: desviacionPct(gastoActual, referencia.referencia),
      tipo,
      seRepite: clasificacion.tipo === 'sube' || clasificacion.tipo === 'baja',
    });
  }
  filas.sort((a, b) => b.desviacion - a.desviacion);
  return filas;
}

export type FilaNomina = {
  payee: string | null;
  porMes: IntegerAmount[]; // alineado con `meses`
  total: IntegerAmount;
};

/**
 * Ingresos (categorías de ingreso) por beneficiario y mes. Ordenado por total.
 */
export function nominasPorPayee(
  movimientos: readonly Movimiento[],
  meses: readonly string[],
  categorias: Categorias,
): FilaNomina[] {
  const indice = new Map(meses.map((mes, i) => [mes, i] as const));
  const porPayee = new Map<string | null, IntegerAmount[]>();
  for (const m of movimientos) {
    const i = indice.get(m.mes);
    if (i === undefined || !esDeIngreso(m.categoria, categorias)) {
      continue;
    }
    let fila = porPayee.get(m.payee);
    if (!fila) {
      fila = meses.map(() => 0);
      porPayee.set(m.payee, fila);
    }
    fila[i] += m.importe;
  }
  const filas: FilaNomina[] = [];
  for (const [payee, porMes] of porPayee) {
    const total = porMes.reduce((a, b) => a + b, 0);
    if (total !== 0) {
      filas.push({ payee, porMes, total });
    }
  }
  filas.sort((a, b) => b.total - a.total);
  return filas;
}

/** Variación de un valor frente al anterior, en fracción; null sin referencia. */
export function variacion(actual: number, anterior: number): number | null {
  return desviacionPct(actual, anterior);
}

export type AcumuladoAno = {
  ingresos: IntegerAmount;
  gastos: IntegerAmount;
  diferencia: IntegerAmount;
};

/**
 * Lo que va de año (de enero al mes en curso, incluido) a partir de los
 * totales mes a mes: ingresos, gastos y lo que queda.
 */
export function acumuladoAno(
  porMes: readonly MesIngresosGastos[],
  mesActual: string,
): AcumuladoAno {
  const ano = mesActual.slice(0, 4);
  let ingresos = 0;
  let gastos = 0;
  for (const m of porMes) {
    if (m.mes.slice(0, 4) === ano && m.mes <= mesActual) {
      ingresos += m.ingresos;
      gastos += m.gastos;
    }
  }
  return { ingresos, gastos, diferencia: ingresos - gastos };
}
